import { style, keyframes } from '@vanilla-extract/css';
import { recipe } from '@vanilla-extract/recipes';
import { color, config, DefaultReset, toRem } from 'folds';

export const MarginSpaced = style({
  marginBottom: config.space.S200,
  marginTop: config.space.S200,
  selectors: {
    '&:first-child': {
      marginTop: 0,
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },
});

export const Paragraph = style([DefaultReset]);

export const Heading = style([
  DefaultReset,
  MarginSpaced,
  {
    marginTop: config.space.S400,
    selectors: {
      '&:first-child': {
        marginTop: 0,
      },
    },
  },
]);

export const BlockQuote = style([
  DefaultReset,
  MarginSpaced,
  {
    paddingLeft: config.space.S200,
    borderLeft: `${config.borderWidth.B700} solid ${color.SurfaceVariant.ContainerLine}`,
    fontStyle: 'italic',
  },
]);

const BaseCode = style({
  color: color.Background.OnContainer,
  background: color.Background.ContainerActive,
  borderRadius: config.radii.R300,
});
const CodeFont = style({
  fontFamily: 'monospace',
});

export const Code = style([
  DefaultReset,
  BaseCode,
  CodeFont,
  {
    padding: `2px ${config.space.S100}`,
  },
]);

export const Spoiler = recipe({
  base: [
    DefaultReset,
    {
      padding: `0 ${config.space.S100}`,
      backgroundColor: color.SurfaceVariant.ContainerActive,
      borderRadius: config.radii.R300,
      selectors: {
        '&[aria-pressed=true]': {
          color: 'transparent',
        },
      },
    },
  ],
  variants: {
    active: {
      true: {
        color: 'transparent',
      },
    },
  },
});

export const CodeBlock = style([
  DefaultReset,
  BaseCode,
  MarginSpaced,
  {
    fontStyle: 'normal',
    position: 'relative',
    overflow: 'hidden',
  },
]);
export const CodeBlockHeader = style([
  {
    padding: `0 ${config.space.S200} 0 ${config.space.S300}`,
    borderBottomWidth: config.borderWidth.B300,
    gap: config.space.S200,
  },
]);
export const CodeBlockInternal = style([
  CodeFont,
  {
    padding: `${config.space.S200} ${config.space.S200} 0`,
    minWidth: toRem(200),
  },
]);

export const CodeBlockBottomShadow = style({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  height: config.space.S700,
  padding: `0 ${config.space.S200} ${config.space.S200}`,
  pointerEvents: 'none',

  background: `linear-gradient(to top, ${color.SurfaceVariant.Container} 0%, ${color.SurfaceVariant.Container} 20%, #00000000 100%)`,
});
export const CodeBlockExpandButton = style({
  pointerEvents: 'auto',
});
export const CodeBlockCollapseFooter = style({
  display: 'flex',
  justifyContent: 'center',
  padding: `${config.space.S100} ${config.space.S200} ${config.space.S200}`,
});

export const List = style([
  DefaultReset,
  MarginSpaced,
  {
    padding: `0 ${config.space.S100}`,
    paddingLeft: config.space.S600,
  },
]);

export const Img = style([
  DefaultReset,
  MarginSpaced,
  {
    maxWidth: toRem(296),
    borderRadius: config.radii.R300,
  },
]);

export const InlineChromiumBugfix = style({
  fontSize: 0,
  lineHeight: 0,
});

export const Mention = recipe({
  base: [
    DefaultReset,
    {
      color: color.Primary.Main,
      padding: `0 ${toRem(2)}`,
      fontWeight: config.fontWeight.W500,
    },
  ],
  variants: {
    focus: {
      true: {
        boxShadow: `0 0 0 ${config.borderWidth.B300} ${color.SurfaceVariant.OnContainer}`,
      },
    },
  },
});

export const Command = recipe({
  base: [
    DefaultReset,
    {
      padding: `0 ${toRem(2)}`,
      borderRadius: config.radii.R300,
      fontWeight: config.fontWeight.W500,
    },
  ],
  variants: {
    focus: {
      true: {
        boxShadow: `0 0 0 ${config.borderWidth.B300} ${color.Warning.OnContainer}`,
      },
    },
    active: {
      true: {
        backgroundColor: color.Warning.Container,
        color: color.Warning.OnContainer,
        boxShadow: `0 0 0 ${config.borderWidth.B300} ${color.Warning.ContainerLine}`,
      },
    },
  },
});

export const EmoticonBase = style([
  DefaultReset,
  {
    display: 'inline-block',
    padding: '0.05rem',
    height: '1em',
    verticalAlign: 'middle',
  },
]);

export const Emoticon = recipe({
  base: [
    DefaultReset,
    {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',

      height: '1em',
      minWidth: '1em',
      fontSize: '1.33em',
      lineHeight: '1em',
      verticalAlign: 'middle',
      position: 'relative',
      top: '-0.35em',
      borderRadius: config.radii.R300,
    },
  ],
  variants: {
    focus: {
      true: {
        boxShadow: `0 0 0 ${config.borderWidth.B300} ${color.SurfaceVariant.OnContainer}`,
      },
    },
  },
});

export const EmoticonImg = style([
  DefaultReset,
  {
    height: '1em',
    cursor: 'default',
  },
]);

export const highlightText = style([
  DefaultReset,
  {
    backgroundColor: 'yellow',
    color: 'black',
  },
]);

const toolCallRunningPulse = keyframes({
  '0%, 100%': { opacity: 1 },
  '50%': { opacity: 0.5 },
});

export const ToolCallIcon = style({
  display: 'inline-flex',
  alignItems: 'center',
});

export const ToolCallRunningIcon = style([
  ToolCallIcon,
  {
    animation: `${toolCallRunningPulse} 1.5s ease-in-out infinite`,
  },
]);

export const ToolCallTitle = style({
  opacity: 0.6,
  fontSize: toRem(13),
});

export const ToolCall = recipe({
  base: [
    DefaultReset,
    {
      display: 'inline-flex',
      alignItems: 'center',
      gap: config.space.S100,
      padding: `0 ${config.space.S200}`,
      borderRadius: config.radii.R300,
      fontWeight: config.fontWeight.W500,
      verticalAlign: 'middle',
      backgroundColor: color.SurfaceVariant.Container,
      color: color.SurfaceVariant.OnContainer,
      boxShadow: `0 0 0 ${config.borderWidth.B300} ${color.SurfaceVariant.ContainerLine}`,
    },
  ],
  variants: {},
});

export const TableContainer = style({
  borderRadius: config.radii.R300,
  overflow: 'hidden',
  border: `${config.borderWidth.B300} solid ${color.SurfaceVariant.ContainerLine}`,
});

export const Table = style([
  DefaultReset,
  MarginSpaced,
  {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
  },
]);

export const TableCell = style([
  DefaultReset,
  {
    borderRight: `${config.borderWidth.B300} solid ${color.SurfaceVariant.ContainerLine}`,
    borderBottom: `${config.borderWidth.B300} solid ${color.SurfaceVariant.ContainerLine}`,
    padding: `${config.space.S100} ${config.space.S200}`,
    selectors: {
      '&:last-child': {
        borderRight: 'none',
      },
      'table > tr:last-child > &, tbody > tr:last-child > &': {
        borderBottom: 'none',
      },
    },
  },
]);

export const TableHeaderCell = style([
  TableCell,
  {
    fontWeight: config.fontWeight.W600,
    backgroundColor: color.SurfaceVariant.Container,
  },
]);

export const HorizontalRule = style([
  DefaultReset,
  MarginSpaced,
  {
    border: 'none',
    borderTop: `${config.borderWidth.B300} solid ${color.SurfaceVariant.ContainerLine}`,
    margin: `${config.space.S500} 0`,
  },
]);
